import React, { useState, useEffect } from "react";
import { Parallax } from "react-parallax";
import Footer from "./Footer";
import Header from "./Header";
import image2 from "../Assets/page_bg.jpg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../Assets/pdf-header.jpg";
import "./Thanks.css";
import { FaCheckCircle, FaPrint, FaHome, FaShoppingCart, FaSpinner, FaPhone, FaBarcode } from "react-icons/fa";
// import MobileNavigation from "./MobileNavigation";

function Thanks() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const order_id = params.get("order_id");

  const [orderData, setOrderData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const ADMIN_API_BASE_URL =  process.env.REACT_APP_ADMIN_API_BASE_URL;

  // Calculate Net Total (Subtotal - Discount)
  const calculateNetTotal = () => {
    if (!orderData) return 0;
    const subtotal = parseFloat(orderData.subtotal) || 0;
    const discount = parseFloat(orderData.discount_amt) || 0;
    return subtotal - discount;
  };

  useEffect(() => {
    if (!order_id) {
      setError("No order ID provided in the URL.");
      setLoading(false);
      return;
    }

    const fetchSettingsDetails = async () => {
      try {
        const response = await axios.post(
          `${ADMIN_API_BASE_URL}setting_detail.php`
        );
        if (response.data && response.data.head && response.data.head.code === 200) {
          setSettingsData(response.data.body);
        }
      } catch (error) {
        console.error("Error fetching settings details:", error);
      }
    };

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.post(
          // `${ADMIN_API_BASE_URL}order_details.php`,
          `https://dharshancrackers.com/admin_api/order_details.php`,
          { order_id: String(order_id) },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && response.data.head && response.data.head.code === 200) {
          setOrderData(response.data.body);
        } else {
          setError("Failed to fetch order details.");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        setError("Failed to load order details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
    fetchSettingsDetails();
  }, [order_id]);

  // Open invoice in new window for printing
  const handlePrint = () => {
    const printWindow = window.open(
      `/invoice?order_id=${order_id}`,
      '_blank',
      'width=800,height=600,scrollbars=yes,resizable=yes'
    );
    if (printWindow) {
      printWindow.focus();
    }
  };

  // Loading Screen
  if (loading) {
    return (
      <div className="print-loading-container">
        <div className="print-loading-spinner">
          <FaSpinner className="print-spin-icon" />
        </div>
        <div className="print-loading-text">Loading your order details...</div>
        <p className="print-loading-subtext">Please wait while we prepare your invoice</p>
        <p className="print-order-id">
          Order ID: <strong>{order_id}</strong>
        </p>
      </div>
    );
  }

  // Error Screen
  if (error) {
    return (
      <div className="print-error-container">
        <div className="print-error-icon">⚠️</div>
        <h3 className="print-error-title">Unable to Load Order Details</h3>
        <p className="print-error-message">{error}</p>
        {order_id && (
          <div className="print-error-info">
            <p className="print-error-order">
              Your order was submitted with ID: <strong>{order_id}</strong>
            </p>
            <p className="print-error-save">
              Please save this order ID for future reference.
            </p>
          </div>
        )}
        <p className="print-error-contact">
          Our team will contact you soon regarding your order.
        </p>
        <div className="print-error-actions">
          <button
            className="print-btn-primary"
            onClick={() => navigate("/")}
          >
            <FaHome className="print-btn-icon" />
            Back to Home
          </button>
          <button
            className="print-btn-outline"
            onClick={() => navigate("/products")}
          >
            <FaShoppingCart className="print-btn-icon" />
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return null;
  }

  const netTotal = calculateNetTotal();

  // Success Screen
  return (
    <div className="print-thanks-page">
      <Header />

      {/* Parallax Header */}
      <Parallax
        bgImage={image2}
        bgImageAlt="background"
        strength={300}
        className="print-parallax-header"
        style={{ minHeight: "100px" }}
      >
        <div style={{ height: "230px" }}>
          <h1 className="print-page-title">
            Thank You!
          </h1>
          <p className="print-breadcrumb">
            <Link to="/" className="print-breadcrumb-link">Home</Link>
            <i className="bi bi-chevron-right print-breadcrumb-icon"></i>
            Order Confirmation
          </p>
        </div>
      </Parallax>

      <div className="print-container">
        {/* Success Message */}
        <div className="print-success-alert">
          <div className="print-success-content">
            <FaCheckCircle className="print-success-icon" />
            <div className="print-success-text">
              <h4 className="print-success-title">Order Placed Successfully!</h4>
              <p className="print-success-message">
                Thank you for your enquiry. We will contact you soon. Your order ID is:
                <strong className="print-success-orderid">{orderData.order_id}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Print Button - Opens in new window */}
        <div className="print-action-bar">
          <Link target="_blank" className="print-action-btn" to={`/invoice?order_id=${order_id}`}>
            <FaPrint className="print-action-icon" /> Print / Save as PDF
          </Link>

          <Link to="/" className="print-action-link">
            <FaHome className="print-action-icon" />
            Back to Home
          </Link>
        </div>

        {/* Invoice Display */}
        <div className="print-invoice-wrapper">
          <div className="print-watermark">Dharshan Crackers</div>
          <div className="print-invoice-container">
            <table className="print-invoice-table">
              <tbody>
                {/* Header with Logo */}
                <tr>
                  <td className="print-invoice-header">
                    <img
                      src={Logo}
                      alt="Dharshan Crackers Logo"
                      className="print-company-logo"
                      onLoad={() => setImageLoaded(true)}
                    />
                  </td>
                </tr>

                {/* Order Info with Phone Number */}
                <tr>
                  <td className="print-order-info">
                    <div className="print-order-row">
                      <div>
                        <strong>Order ID:</strong> {orderData.order_id}
                      </div>
                      {/* <div>
                        <strong><FaPhone className="print-breadcrumb-icon" /> Phone:</strong> {settingsData?.phone || 'N/A'}
                      </div> */}
                      <div>
                        <strong>Date:</strong> {orderData.orderdate}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Billing Information */}
                <tr>
                  <td className="print-billing-section">
                    <div className="print-billing-card">
                      <h3 className="print-billing-title">Billing Information</h3>
                      <p className="print-billing-name">{orderData.billing_name}</p>
                      <p className="print-billing-address">
                        {orderData.billing_address}, {orderData.billing_city},{" "}
                        {orderData.billing_state}, {orderData.billing_pincode}
                      </p>
                      <p className="print-billing-mobile">
                        <strong>Mobile:</strong> {orderData.mobile}
                      </p>
                      <p className="print-billing-email">
                        <strong>Email:</strong> {orderData.email}
                      </p>
                      {orderData.coupen_code && (
                        <p className="print-billing-coupon">
                          <strong>Coupon Code:</strong> {orderData.coupen_code}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Product Table with Product Code Column */}
                <tr>
                  <td className="print-products-section">
                    <table className="print-product-grid">
                      <thead>
                        <tr>
                          <th className="print-col-sno">S.No</th>
                          {/* <th className="print-col-product-Code">Product Code</th> */}
                          <th className="print-col-product">Product Name</th>
                          <th className="print-col-qty">Qty</th>
                          <th className="print-col-price text-right">Price</th>
                          <th className="print-col-total text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData.order_details && orderData.order_details.map((item, index) => (
                          <tr key={index} className="print-product-row">
                            <td className="print-cell-sno">{index + 1}</td>
                            {/* <td className="print-cell-product">
                              {item.product_code ? (
                                <span className="product-code-badge">
                                  <FaBarcode className="print-breadcrumb-icon" /> {item.product_code}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td> */}
                              <td className="print-cell-product">{item.product_name}</td>
                            <td className="print-cell-qty">{item.qty}</td>
                            <td className="print-cell-price text-right">
                              ₹ {parseFloat(item.amount).toFixed(2)}
                            </td>
                            <td className="print-cell-total text-right">
                              ₹ {parseFloat(item.totalamount).toFixed(2)}
                            </td>
                          </tr>
                        ))}

                        {/* Sub Total */}
                        <tr className="print-summary-row">
                          <td colSpan="4" className="print-summary-label text-right">
                            <strong>Sub-Total</strong>
                          </td>
                          <td className="print-summary-value text-right">
                            ₹ {parseFloat(orderData.subtotal).toFixed(2)}
                          </td>
                        </tr>

                        {/* Discount */}
                        {orderData.discount_amt > 0 && (
                          <tr className="print-discount-row">
                            <td colSpan="4" className="print-summary-label text-right">
                              <strong>Discount ({orderData.discount_percentage}%)</strong>
                              <span style={{ fontSize: "11px",fontWeight:"bold", color: "orangered", marginLeft: "6px" }}>
    (Applied Products only)
  </span>
                            </td>
                            <td className="print-summary-value print-discount-value text-right">
                              ₹ {parseFloat(orderData.discount_amt).toFixed(2)}
                            </td>
                          </tr>
                          
                        )}

                        {/* NET TOTAL - NEW ROW */}
                        <tr className="print-nettotal-row">
                          <td colSpan="4" className="print-summary-label text-right">
                            <strong className="print-nettotal-text">Net Total</strong>
                          </td>
                          <td className="print-summary-value print-nettotal-value text-right">
                            <strong className="print-nettotal-amount">
                              ₹ {netTotal.toFixed(2)}
                            </strong>
                          </td>
                        </tr>

                        {/* Packing Charges */}
                        {orderData.packing_amt > 0 && (
                          <tr className="print-packing-row">
                            <td colSpan="4" className="print-summary-label text-right">
                              <strong>Packing ({orderData.packing_percentage}%)</strong>
                            </td>
                            <td className="print-summary-value text-right">
                              ₹ {parseFloat(orderData.packing_amt).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* CGST */}
                        {Number(orderData.cgst_amt) > 0 && (
                          <tr className="print-tax-row">
                            <td colSpan="4" className="print-summary-label text-right">
                              <strong>CGST</strong>
                            </td>
                            <td className="print-summary-value text-right">
                              ₹ {parseFloat(orderData.cgst_amt).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* SGST */}
                        {Number(orderData.sgst_amt) > 0 && (
                          <tr className="print-tax-row">
                            <td colSpan="4" className="print-summary-label text-right">
                              <strong>SGST</strong>
                            </td>
                            <td className="print-summary-value text-right">
                              ₹ {parseFloat(orderData.sgst_amt).toFixed(2)}
                            </td>
                          </tr>
                        )}

                        {/* Grand Total */}
                        <tr className="print-grandtotal-row">
                          <td colSpan="4" className="print-grandtotal-label text-right">
                            <strong>Grand Total</strong>
                          </td>
                          <td className="print-grandtotal-value text-right">
                            <strong>
                              ₹ {(orderData.total % 1 >= 0.5
                                ? Math.ceil(orderData.total).toFixed(2)
                                : Math.floor(orderData.total).toFixed(2))}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Footer Message */}
            <div className="print-footer-message">
              <p className="print-diwali-wish">This Estimate is Valid For 3 Days Only</p>
              <p className="print-diwali-wish">Happy Diwali... Safety Diwali...</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      {/* <MobileNavigation /> */}
    </div>
  );
}

export default Thanks;